// src/services/CopyTradeService.js
import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import {
  getSettings,
  updateSettings,
  appendTrade,
  getNextTradeNumber,
  deleteJournalRow,   
} from "./FirestoreService";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const toPct = (num, denom) =>
  denom > 0 && Number.isFinite(num) ? `${((num / denom) * 100).toFixed(2)}%` : "";

/**
 * Compute a stable fingerprint for a leader-trade copy.
 * This lets us dedupe on create and find the same follower row later for updates.
 */
function makeFingerprint(leaderAccountId, leaderTradeNum) {
  return `copy:${leaderAccountId}:${leaderTradeNum}`;
}

/**
 * Try to find the follower's journal doc that corresponds to the given leader trade.
 * We search by:
 *   1) copyFingerprint (preferred),
 *   2) Leader Trade # (then filter by Copied From Account),
 *   3) Copied From Trade # (legacy), then filter.
 *
 * Returns { id, data } or null.
 */
async function findFollowerCopy({ userId, followerId, leaderAccountId, leaderTradeNum }) {
  const jr = collection(db, "users", userId, "accounts", followerId, "journal");

  // 1) copyFingerprint (single-field query; no composite index needed)
  try {
    const fp = makeFingerprint(leaderAccountId, leaderTradeNum);
    const snap = await getDocs(query(jr, where("copyFingerprint", "==", fp), limit(1)));
    if (!snap.empty) return { id: snap.docs[0].id, data: snap.docs[0].data() };
  } catch {}

  // 2) Leader Trade # (then filter by account)
  try {
    const snap = await getDocs(query(jr, where("Leader Trade #", "==", leaderTradeNum), limit(5)));
    const hit = snap.docs.find(d => {
      const x = d.data() || {};
      return (
        String(x["Copied From Account"] || x["Copied From Leader Id"] || "") ===
        String(leaderAccountId)
      );
    });
    if (hit) return { id: hit.id, data: hit.data() };
  } catch {}

  // 3) Copied From Trade # (legacy) then filter by account
  try {
    const snap = await getDocs(query(jr, where("Copied From Trade #", "==", leaderTradeNum), limit(5)));
    const hit = snap.docs.find(d => {
      const x = d.data() || {};
      return (
        String(x["Copied From Account"] || x["Copied From Leader Id"] || "") ===
        String(leaderAccountId)
      );
    });
    if (hit) return { id: hit.id, data: hit.data() };
  } catch {}

  return null;
}

export async function deleteLeaderTradeFromFollowers({
  userId,
  leaderAccountId,
  leaderTrade,
  accountType,
}) {
  if (!userId || !leaderAccountId || !leaderTrade) return;

  // Same CopyTrade config checks as copyTradeToFollowers
  const leaderSettings = await getSettings(userId, leaderAccountId);
  const cfg = leaderSettings?.CopyTrade || leaderSettings?.settings?.CopyTrade;
  if (!cfg || cfg.role !== "leader") return;

  const configuredGroup = String(cfg.group || cfg.accountType || "").toLowerCase();
  if (configuredGroup && accountType && configuredGroup !== String(accountType).toLowerCase()) {
    return;
  }

  const followerIds = await resolveFollowerIds({ userId, leaderAccountId, cfg, accountType });
  if (!followerIds.length) return;

  const leaderTradeNum = Number(
    leaderTrade?.["Trade #"] ?? leaderTrade?.tradeNum ?? NaN
  );
  if (!Number.isFinite(leaderTradeNum)) return;

  for (const fid of followerIds) {
    if (!fid || fid === leaderAccountId) continue;

    try {
      const fset = await getSettings(userId, fid);
      if (!fset) continue;

      // Guard: account type must match if provided
      const fType = (fset["type"] || fset?.settings?.type || "").toLowerCase();
      if (accountType && fType && fType !== String(accountType).toLowerCase()) continue;

      const existing = await findFollowerCopy({
        userId,
        followerId: fid,
        leaderAccountId,
        leaderTradeNum,
      });
      if (!existing) continue;

      const reward = Number(existing.data?.["Actual Reward"] || 0);

      // 1) Delete the follower's journal row
      await deleteJournalRow(userId, fid, existing.id);

      // 2) Adjust follower account balances by *removing* this trade's P&L
      const prevRB = Number(fset["Running Balance"] || 0);
      const nextRB = round2(prevRB - reward);

      const hasPRB = typeof fset["Prop Running Balance"] !== "undefined";
      const prevPRB = Number(fset["Prop Running Balance"] ?? prevRB);
      const nextPRB = round2(prevPRB - reward);

      const patch = hasPRB
        ? { "Running Balance": nextRB, "Prop Running Balance": nextPRB }
        : { "Running Balance": nextRB };

      await updateSettings(userId, fid, patch);
    } catch (err) {
      console.error("[CopyTradeService] follower delete failed:", fid, err);
    }
  }
}


/**
 * Get the list of followers either from the leader's settings(followerIds),
 * or by scanning accounts where CopyTrade.role === 'follower' and leaderId matches.
 */
async function resolveFollowerIds({ userId, leaderAccountId, cfg, accountType }) {
  let followerIds = Array.isArray(cfg?.followerIds) ? [...cfg.followerIds] : [];
  if (followerIds.length) return followerIds;

  try {
    const snap = await getDocs(collection(db, "users", userId, "accounts"));
    followerIds = snap.docs
      .map(d => ({ id: d.id, data: d.data() || {} }))
      .filter(({ id, data }) => {
        const s = data.settings || {};
        const c = s.CopyTrade || data.CopyTrade;
        const t = (s.type || data.type || "").toLowerCase();
        return (
          id !== leaderAccountId &&
          c?.role === "follower" &&
          c?.leaderId === leaderAccountId &&
          (!accountType || t === String(accountType).toLowerCase())
        );
      })
      .map(({ id }) => id);
  } catch {
    /* ignore */
  }
  return followerIds;
}

/**
 * Copy a leader’s trade to all followers OR update their existing copies.
 *
 * On create (default):
 *   - Idempotent: if a follower already has this leader trade, we update it instead of inserting.
 * On update (mode === "update"):
 *   - If the follower copy exists, we update that row and adjust follower balances by the delta difference.
 *   - If the follower copy is missing, we SKIP (no create) so edits don't create new rows.
 *
 * @param {object} p
 * @param {string} p.userId
 * @param {string} p.leaderAccountId
 * @param {object} p.leaderTrade           trade object as saved on the leader
 * @param {number} p.delta                  leader trade P&L for this trade (±)
 * @param {"futures"|"forex"} p.accountType group filter (optional but recommended)
 * @param {"create"|"update"} [p.mode]      default "create"
 */
export async function copyTradeToFollowers({
  userId,
  leaderAccountId,
  leaderTrade,
  delta,
  accountType,
  mode = "create",
}) {
  if (!userId || !leaderAccountId) return;

  // --- Load leader's CopyTrade config
  const leaderSettings = await getSettings(userId, leaderAccountId);
  const cfg = leaderSettings?.CopyTrade || leaderSettings?.settings?.CopyTrade;
  if (!cfg || cfg.role !== "leader") return;

  const configuredGroup = String(cfg.group || cfg.accountType || "").toLowerCase();
  if (configuredGroup && accountType && configuredGroup !== String(accountType).toLowerCase()) {
    return; // leader configured for another group
  }

  const followerIds = await resolveFollowerIds({ userId, leaderAccountId, cfg, accountType });
  if (!followerIds.length) return;

  const leaderTradeNum = Number(
    leaderTrade?.["Trade #"] ?? leaderTrade?.tradeNum ?? NaN
  );
  const fp = Number.isFinite(leaderTradeNum) ? makeFingerprint(leaderAccountId, leaderTradeNum) : null;
  const deltaNum = round2(Number(delta || 0));

  for (const fid of followerIds) {
    if (!fid || fid === leaderAccountId) continue; // never copy to self

    try {
      // Load follower settings (flattened) for balances
      const fset = await getSettings(userId, fid);
      if (!fset) continue;

      // Guard: must be the same market type if provided
      const fType = (fset["type"] || fset?.settings?.type || "").toLowerCase();
      if (accountType && fType && fType !== String(accountType).toLowerCase()) continue;

      // Try to find an existing follower copy of this leader trade
      const existing = Number.isFinite(leaderTradeNum)
        ? await findFollowerCopy({ userId, followerId: fid, leaderAccountId, leaderTradeNum })
        : null;

      if (existing) {
        // ---------- UPDATE EXISTING FOLLOWER ROW ----------
        const before = Number(existing.data?.["Balance Before Trade"] || 0);
        const prevReward = Number(existing.data?.["Actual Reward"] || 0);
        const diff = round2(deltaNum - prevReward); // how much to adjust the follower's ledger by
        const newActualBalance = round2(before + deltaNum);

        // Build the patch
        const patch = {
          "Actual Reward": deltaNum,
          "Actual Balance": newActualBalance,
          "Running Balance": newActualBalance,
          "Actual Percent Growth": toPct(deltaNum, before),
        };

        // Refresh provenance if missing (helps future updates)
        if (fp) {
          patch["copyFingerprint"] = existing.data?.copyFingerprint || fp;
          patch["Leader Trade #"] = leaderTradeNum;
          patch["leaderTradeNum"] = leaderTradeNum;
          patch["Copied From Account"] = existing.data?.["Copied From Account"] || leaderAccountId;
          patch["Copied From Leader Id"] = existing.data?.["Copied From Leader Id"] || leaderAccountId;
          patch["Copied From Trade #"] = existing.data?.["Copied From Trade #"] ?? leaderTradeNum;
        }

        // Optionally carry a small, safe whitelist from the leader on edit
        const CARRY_FIELDS = [
          "Product", "Position", "Size",
          "Entry Time", "Exit Time", "Duration (h:mm)",
          "Entry Strategy", "Exit Strategy",
          "Win", "Loss",
          "Timeframe", "Session",
        ];
        for (const k of CARRY_FIELDS) {
          if (leaderTrade && Object.prototype.hasOwnProperty.call(leaderTrade, k)) {
            patch[k] = leaderTrade[k];
          }
        }

        await updateDoc(
          doc(db, "users", userId, "accounts", fid, "journal", existing.id),
          patch
        );

        // Adjust follower account balances by the difference
        const prevRB = Number(fset["Running Balance"] || 0);
        const nextRB = round2(prevRB + diff);

        const hasPRB = typeof fset["Prop Running Balance"] !== "undefined";
        const prevPRB = Number(fset["Prop Running Balance"] ?? prevRB);
        const nextPRB = round2(prevPRB + diff);

        const settingsPatch = hasPRB
          ? { "Running Balance": nextRB, "Prop Running Balance": nextPRB }
          : { "Running Balance": nextRB };

        await updateSettings(userId, fid, settingsPatch);
        continue; // done with this follower
      }

      // If we're in update-only mode and no doc exists, SKIP creating a new one
      if (mode === "update") continue;

      // ---------- CREATE FOLLOWER ROW (IDEMPOTENT) ----------
      const prevRB = Number(fset["Running Balance"] || 0);
      const nextRB = round2(prevRB + deltaNum);

      const hasPRB = typeof fset["Prop Running Balance"] !== "undefined";
      const prevPRB = Number(fset["Prop Running Balance"] ?? prevRB);
      const nextPRB = round2(prevPRB + deltaNum);

      // Pick follower trade number
      let followerTradeNum;
      try {
        followerTradeNum = await getNextTradeNumber(userId, fid);
      } catch {
        followerTradeNum = Date.now();
      }

      // Start with the leader trade fields but overwrite ledger-specific ones for the follower
      const copied = { ...(leaderTrade || {}) };

      // Always overwrite follower's own numbering + ledger snapshot
      copied["Trade #"] = followerTradeNum;
      copied["tradeNum"] = followerTradeNum;
      copied["Balance Before Trade"] = prevRB;
      copied["Actual Reward"] = deltaNum;
      copied["Actual Balance"] = nextRB;
      copied["Running Balance"] = nextRB;
      copied["Actual Percent Growth"] = toPct(deltaNum, prevRB);

      // Provenance to make future calls idempotent and enable updates
      if (Number.isFinite(leaderTradeNum)) {
        copied["Leader Trade #"] = leaderTradeNum;
        copied["leaderTradeNum"] = leaderTradeNum;
        copied["Copied From Account"] = leaderAccountId;
        copied["Copied From Leader Id"] = leaderAccountId;
        copied["Copied From Trade #"] = leaderTradeNum;
        copied["copyFingerprint"] = fp;
      } else {
        // still record minimal provenance
        copied["Copied From Leader Id"] = leaderAccountId;
        copied["Copied From Account"] = leaderAccountId;
      }

      // Idempotency guard (if someone called us twice during a save)
      if (fp) {
        try {
          const jr = collection(db, "users", userId, "accounts", fid, "journal");
          const dup = await getDocs(query(jr, where("copyFingerprint", "==", fp), limit(1)));
          if (!dup.empty) {
            // Switch to update path if it already exists (rare race)
            const { id, data } = { id: dup.docs[0].id, data: dup.docs[0].data() };
            const before = Number(data?.["Balance Before Trade"] || 0);
            const prevReward = Number(data?.["Actual Reward"] || 0);
            const diff = round2(deltaNum - prevReward);
            const newActualBalance = round2(before + deltaNum);

            await updateDoc(doc(db, "users", userId, "accounts", fid, "journal", id), {
              "Actual Reward": deltaNum,
              "Actual Balance": newActualBalance,
              "Running Balance": newActualBalance,
              "Actual Percent Growth": toPct(deltaNum, before),
            });

            const prevRB2 = Number(fset["Running Balance"] || 0);
            const nextRB2 = round2(prevRB2 + diff);
            const prevPRB2 = Number(fset["Prop Running Balance"] ?? prevRB2);
            const nextPRB2 = round2(prevPRB2 + diff);
            const patch2 = hasPRB
              ? { "Running Balance": nextRB2, "Prop Running Balance": nextPRB2 }
              : { "Running Balance": nextRB2 };
            await updateSettings(userId, fid, patch2);
            continue;
          }
        } catch {
          /* non-fatal, fall through to append */
        }
      }

      // Create the follower journal row
      await appendTrade(userId, fid, copied);

      // Patch follower balances (settings)
      const settingsPatch = hasPRB
        ? { "Running Balance": nextRB, "Prop Running Balance": nextPRB }
        : { "Running Balance": nextRB };
      await updateSettings(userId, fid, settingsPatch);
    } catch (err) {
      console.error("[CopyTradeService] follower sync failed:", fid, err);
    }
  }
}
